// Byer (Kilowattere med ansigt) og producenter: møller med snurrende vinger, sol, værk, station.
// Byerne blinker, smiler når de har strøm, bliver bekymrede og sure når mørkeuret løber, og sover i mørket.
// Efter en blackout er de kede af det med stearinlys i vinduerne, og på grøn strøm hopper de af glæde.

import { CanvasTextMetrics, Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import type { GridNode } from '../sim';
import { shedNow } from '../sim/effects';
import type { Atlas } from './atlas';
import type { Fx } from './fx';
import type { P } from './geo';
import { emptyMemory, type LabelNode, type LabelOpts, layoutLabels, shortName, type Size } from './labels';
import { cityLook, isLit } from './look';
import type { CityLook } from './look';
import { C } from './palette';
import { ease } from './tween';
import type { Tweens } from './tween';
import type { View } from './view';

type Mood = 'happy' | 'worried' | 'angry' | 'sleep' | 'sad';

export interface ScreenRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface NodeView {
  id: string;
  kind: GridNode['kind'];
  c: Container;
  under: Graphics;
  body: Sprite;
  bodyKey: string;
  blades: Sprite | null;
  bladeY: number;
  spin: number;
  face: Container | null;
  /** Hjertet over taget, når byens mørkeur er over halvt (spor "sandhed"). */
  heart: Graphics | null;
  look: CityLook | null;
  eyes: Sprite | null;
  mouth: Sprite | null;
  cheeks: Sprite | null;
  zzz: Sprite | null;
  /** Stearinlys i vinduerne under blackout og en tåre på kinden. */
  candles: Container | null;
  candleTier: number;
  tear: Sprite | null;
  /** Hop: t går 0..1 hen over hoppet; amp er højden i px. */
  hop: { t: number; amp: number };
  /** Hvor mange px hoppet løfter huset lige nu (testkrog: huset i hvile). */
  lift: number;
  /** Næste glædeshop på grøn strøm. */
  joyAt: number;
  badges: Container;
  /** Strømmåleren ved en by, der får for lidt: hvor meget af sin strøm den får lige nu. */
  meter: Graphics;
  /** Hvor måleren stod sidst (indeks i drawMeters pladser): den prøves først. */
  meterSpot?: number;
  label: Text;
  pop: { s: number; sx: number; sy: number };
  blinkAt: number;
  blinkUntil: number;
  mood: Mood | null;
  smokeAt: number;
  shown: boolean;
  /** Byen mangler strøm eller er mørk: navnet vises altid, for det er den, spilleren skal finde. */
  urgent?: boolean;
  /** Navnets former (fuldt, brudt, kort) og hvilken der står nu. */
  variants: LabelVariants | null;
  variant: number;
}

const LABEL_BASE = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: '600' as const,
  fontSize: 14,
  fill: C.ink,
  stroke: { color: C.white, width: 4, join: 'round' as const },
  letterSpacing: 0.2,
  align: 'center' as const,
  lineHeight: 17,
};

/**
 * Navnets fire former (Spor: kortet): det fulde navn på én linje (lange navne brydes ved 140 px), det
 * samme brudt smalt i to linjer, den korte form ("DC Hinnerup") og, som sidste udvej i en tæt klynge
 * på telefonen (Spor: telefonkort), den korte form med lille skrift. En trængt node tager den første,
 * der står frit, i stedet for at lægge sig oven i naboen.
 */
const LABEL_STYLES = [
  new TextStyle({ ...LABEL_BASE, wordWrap: true, wordWrapWidth: 140, breakWords: true }),
  new TextStyle({ ...LABEL_BASE, wordWrap: true, wordWrapWidth: 76, breakWords: false }),
  new TextStyle({ ...LABEL_BASE, wordWrap: true, wordWrapWidth: 140, breakWords: true }),
  new TextStyle({ ...LABEL_BASE, fontSize: 12, lineHeight: 15, stroke: { ...LABEL_BASE.stroke, width: 3 }, wordWrap: true, wordWrapWidth: 140, breakWords: true }),
];

interface LabelVariants {
  name: string;
  texts: (string | null)[];
  sizes: (Size | null)[];
}

/** Navnets former og deres mål (målt én gang pr. navn). */
function labelVariants(name: string): LabelVariants {
  const size = (t: string, i: number): Size => {
    const m = CanvasTextMetrics.measureText(t, LABEL_STYLES[i] as TextStyle);
    return { w: Math.ceil(m.width) + 1, h: Math.ceil(m.height) + 1 };
  };
  const full = size(name, 0);
  const narrow = /[\s-]/.test(name) ? size(name, 1) : null;
  const wrap = narrow && narrow.w < full.w - 8 ? narrow : null;
  const short = shortName(name);
  return {
    name,
    texts: [name, wrap ? name : null, short !== name ? short : null, short],
    sizes: [full, wrap, short !== name ? size(short, 2) : null, size(short, 3)],
  };
}

export { isLit };

/** En ting på kortet med en ejer (node, hold eller trussel), til testkrogen. */
export interface MapObject {
  owner: string;
  type: string;
  box: ScreenRect;
  /** Står stille ved sin ejer (ikke en bil på vej eller en gæst, der løber ind). */
  fixed: boolean;
}

/** En synlig node på skærmen (testkrog). */
export interface NodeBox {
  id: string;
  kind: string;
  urgent: boolean;
  box: ScreenRect;
  /** Huset i hvile (uden hoppet): der, hvor navnene regner med det. */
  rest: ScreenRect;
  label: ScreenRect | null;
  lit: boolean;
  heart: boolean;
  dim: number;
}

export class CitiesLayer {
  readonly c = new Container();
  readonly labels = new Container();
  private views = new Map<string, NodeView>();
  private degree = new Map<string, number>();
  /** Andre ting på skærmen (trusselstal, svævende tal), navneskiltene skal gå uden om. */
  obstacles: ScreenRect[] = [];
  /** De af dem, der står længe (hintet): et navn bliver ikke stående under dem (labels.ts: firm). */
  firm: ScreenRect[] = [];
  /** Det frie felt (uden værktøjslinjen på siden). Navne står helst inden for det. */
  band: { x0: number; y0: number; x1: number; y1: number } | null = null;
  /** Grøn andel af strømmen lige nu (0..1). Høj andel giver glade hop. */
  green = 0;

  constructor(
    private atlas: Atlas,
    private tweens: Tweens,
    private fx: Fx,
  ) {
    this.c.sortableChildren = true;
  }

  /** Synlige navneskilte på skærmen. */
  labelRects(): ScreenRect[] {
    const out: ScreenRect[] = [];
    for (const l of this.labels.children) {
      if (!l.visible || l.alpha < 0.3) continue;
      const b = l.getBounds();
      if (b.width > 0) out.push({ x: b.x, y: b.y, w: b.width, h: b.height });
    }
    return out;
  }

  /**
   * Testkrog (scripts/maptest.mjs): hver synlig nodes krop og navn på skærmen, så testen kan måle,
   * om navnet står ved sin egen node, og om to navne dækker hinanden.
   */
  nodeBoxes(): NodeBox[] {
    const out: NodeBox[] = [];
    for (const nv of this.views.values()) {
      if (!nv.c.visible || nv.pop.s < 0.9) continue;
      const b = nv.body.getBounds();
      const l = nv.label.visible && nv.label.alpha >= 0.3 ? nv.label.getBounds() : null;
      out.push({
        id: nv.id,
        kind: nv.kind,
        urgent: !!nv.urgent,
        box: { x: b.x, y: b.y, w: b.width, h: b.height },
        rest: { x: b.x, y: b.y + nv.lift, w: b.width, h: b.height },
        label: l && l.width > 0 ? { x: l.x, y: l.y, w: l.width, h: l.height } : null,
        // Spor "sandhed" (scripts/truthtest.mjs): ser byen tændt ud, og står hjertet over taget?
        lit: !!nv.look?.lit && nv.body.tint === C.white,
        heart: !!nv.heart?.visible,
        dim: nv.look?.dim ?? 0,
      });
    }
    return out;
  }

  /**
   * Testkrog (scripts/maptest.mjs): de små ting, en node har ved sit hus (strømmåler, hjerte,
   * batteri, svinghjul, skjold), hver med sin ejer, så testen kan se, om de lander på naboen.
   */
  objectBoxes(): MapObject[] {
    const out: MapObject[] = [];
    // Spor: telefonkort: tingene i hvile (uden hoppet), som husene i navnenes og figurernes regning.
    // Et glad hop hvert par sekunder flyttede ellers målet for en figur ved siden af, så den gled frem
    // og tilbage og aldrig stod stille.
    let lift = 0;
    const add = (owner: string, type: string, o: Container | null): void => {
      if (!o || !o.visible || o.destroyed) return;
      const b = o.getBounds();
      if (b.width > 0.5 && b.height > 0.5) out.push({ owner, type, box: { x: b.x, y: b.y + lift, w: b.width, h: b.height }, fixed: true });
    };
    for (const nv of this.views.values()) {
      if (!nv.c.visible || nv.pop.s < 0.9) continue;
      lift = nv.lift;
      add(nv.id, 'meter', nv.meter);
      add(nv.id, 'heart', nv.heart);
      for (const b of nv.badges.children) add(nv.id, 'badge', b as Container);
    }
    return out;
  }

  reset(): void {
    for (const v of this.views.values()) {
      v.c.destroy({ children: true });
      v.label.destroy();
    }
    this.views.clear();
  }

  /** Hit-radius i px for en node. */
  static hitRadius(k: number): number {
    return Math.max(24, 24 * k);
  }

  squash(id: string): void {
    const v = this.views.get(id);
    if (!v) return;
    v.pop.sx = 1.28;
    v.pop.sy = 0.74;
    this.tweens.to(v.pop, { sx: 1, sy: 1 }, 520, ease.outElastic);
  }

  /** Et lille hop (lysbølgen, sejren, grøn glæde). `delay` i ms. */
  hop(id: string, delay = 0, amp = 9): void {
    const v = this.views.get(id);
    if (!v || !v.c.visible) return;
    v.hop.amp = amp;
    this.tweens.fromTo(v.hop, { t: 0 }, { t: 1 }, 420, ease.linear, undefined, delay);
  }

  /** Byer, der vises og har strøm (til lysbølgen). */
  litIds(): string[] {
    return [...this.views.values()].filter((v) => v.c.visible && v.mood === 'happy').map((v) => v.id);
  }

  private bodyKey(n: GridNode): string {
    if (n.kind === 'city') return `city${n.city?.tier ?? 0}_${isLit(n) ? 'lit' : 'dark'}`;
    if (n.kind === 'datacenter') return isLit(n) ? 'dc_lit' : 'dc_dark';
    if (n.kind === 'wind') return 'turbine';
    return n.kind;
  }

  private create(n: GridNode, now: number): NodeView {
    const c = new Container();
    const under = new Graphics();
    const key = this.bodyKey(n);
    const body = this.atlas.sprite(key);
    c.addChild(under, body);
    let blades: Sprite | null = null;
    let bladeY = 0;
    if (n.kind === 'wind' || n.kind === 'offshore') {
      bladeY = n.kind === 'wind' ? -46 : -52;
      blades = this.atlas.sprite('blades');
      blades.position.set(0.5, bladeY);
      blades.rotation = (n.x * 7 + n.y) % 6.28;
      c.addChild(blades);
    }
    let face: Container | null = null;
    let eyes: Sprite | null = null;
    let mouth: Sprite | null = null;
    let cheeks: Sprite | null = null;
    let zzz: Sprite | null = null;
    let tear: Sprite | null = null;
    if (n.city) {
      face = new Container();
      eyes = this.atlas.sprite('eyes_sleep');
      mouth = this.atlas.sprite('mouth_sleep');
      cheeks = this.atlas.sprite('cheeks');
      eyes.position.set(0, -2);
      mouth.position.set(0, 5);
      cheeks.position.set(0, 2);
      face.addChild(cheeks, eyes, mouth);
      face.position.set(0, n.kind === 'datacenter' ? -18 : -14);
      zzz = this.atlas.sprite('zzz');
      zzz.position.set(16, -40);
      c.addChild(face, zzz);
      tear = this.atlas.sprite('tear');
      tear.visible = false;
      face.addChild(tear);
    }
    const badges = new Container();
    const meter = new Graphics();
    c.addChild(badges, meter);
    const label = new Text({ text: n.name, style: LABEL_STYLES[0], resolution: 2 });
    label.anchor.set(0.5, 0);
    this.labels.addChild(label);
    this.c.addChild(c);
    const v: NodeView = {
      id: n.id,
      kind: n.kind,
      c,
      under,
      body,
      bodyKey: key,
      blades,
      bladeY,
      spin: 0,
      face,
      heart: null,
      look: null,
      eyes,
      mouth,
      cheeks,
      zzz,
      candles: null,
      candleTier: -1,
      tear,
      hop: { t: 1, amp: 0 },
      lift: 0,
      joyAt: now + 1500 + Math.random() * 2500,
      badges,
      meter,
      label,
      variants: null,
      variant: 0,
      pop: { s: 0, sx: 1, sy: 1 },
      blinkAt: now + 1000 + Math.random() * 3000,
      blinkUntil: 0,
      mood: null,
      smokeAt: now + Math.random() * 800,
      shown: false,
    };
    return v;
  }

  update(v: View): void {
    const { state, k, now } = v;
    this.degree.clear();
    for (const l of state.lines) {
      if (l.status !== 'ok') continue;
      this.degree.set(l.a, (this.degree.get(l.a) ?? 0) + 1);
      this.degree.set(l.b, (this.degree.get(l.b) ?? 0) + 1);
    }
    const sec = v.dt / 1000;
    const meters: Array<[NodeView, GridNode, boolean]> = [];
    for (const n of state.nodes) {
      let nv = this.views.get(n.id);
      if (!n.active) {
        if (nv) nv.c.visible = nv.label.visible = false;
        continue;
      }
      if (!nv) {
        nv = this.create(n, now);
        this.views.set(n.id, nv);
      }
      if (!nv.shown) {
        nv.shown = true;
        nv.c.visible = true;
        // Dukker op med et lille hop.
        nv.pop.s = 0;
        this.tweens.to(nv.pop, { s: 1 }, 520, ease.outBack, undefined, 60);
        const d = v.disp.get(n.id);
        if (d && state.tick > 0) this.fx.puff(d, 8, C.white, 1.1);
      }
      const p = v.pos.get(n.id);
      if (!p) continue;
      nv.c.visible = true;
      // Hoppet: en parabel op og ned, med lidt squash, når huset lander.
      const ht = nv.hop.t;
      const lift = ht < 1 ? 4 * ht * (1 - ht) * nv.hop.amp : 0;
      const land = ht > 0.82 && ht < 1 ? Math.sin(((ht - 0.82) / 0.18) * Math.PI) * 0.12 : 0;
      const stretch = ht < 0.4 && ht > 0 ? 0.06 * Math.sin((ht / 0.4) * Math.PI) : 0;
      nv.c.position.set(p.x, p.y - lift * k);
      nv.lift = lift * k;
      nv.c.zIndex = p.y;
      nv.c.scale.set(k * 0.92 * nv.pop.s * nv.pop.sx * (1 + land - stretch), k * 0.92 * nv.pop.s * nv.pop.sy * (1 - land + stretch));
      const key = this.bodyKey(n);
      if (key !== nv.bodyKey) {
        nv.body.texture = v.atlas.tex(key);
        nv.bodyKey = key;
      }
      const connected = (this.degree.get(n.id) ?? 0) > 0;
      this.drawUnder(nv, n, v, connected);
      if (nv.blades && n.producer) {
        const wind = n.producer.capMw > 0 ? n.producer.availMw / n.producer.capMw : 0;
        const target = 0.6 + wind * 5.5;
        nv.spin += (target - nv.spin) * Math.min(1, sec * 2);
        if (!v.calm) nv.blades.rotation += nv.spin * sec;
      }
      if (n.kind === 'plant' && n.producer && n.producer.usedMw > 0 && now > nv.smokeAt && !v.calm) {
        nv.smokeAt = now + 650 + Math.random() * 300;
        const d = v.disp.get(n.id);
        if (d) this.fx.smoke(d, 14.5 * 0.92, -48 * 0.92, 1);
      }
      if (n.city) {
        nv.look = cityLook(state, n);
        this.updateLook(nv, v);
        this.updateFace(nv, n, v);
      }
      meters.push([nv, n, connected]);
      this.updateBadges(nv, n, v);
      if (nv.variants?.name !== n.name) {
        nv.variants = labelVariants(n.name);
        nv.variant = -1;
      }
      const cy = n.city;
      nv.urgent = !!cy && cy.demandMw > 0 && (cy.out || cy.servedMw < cy.demandMw * 0.9);
    }
    this.placeLabels(v);
    // Spor: telefonkort: målerne efter navnene, så en måler går uden om navnene i dette billede (før
    // stod Fredericias måler på sit eget navn, der lige havde flyttet sig).
    for (const [nv, n, connected] of meters) this.drawMeter(nv, n, v, connected);
  }

  /**
   * Byen dæmpes, mens mørkeuret løber, og fra halvt ur blinker den med et orange hjerte over taget:
   * det hjerte er det, der ryger, når uret er fuldt (anmelderne, runde 5: "hjerter forsvinder, mens
   * byen ser tændt ud"). Blinket er 2,5 Hz, under grænsen på tre blink i sekundet; med rolig
   * bevægelse står hjertet stille, og kun farven skifter langsomt.
   */
  private updateLook(nv: NodeView, v: View): void {
    const look = nv.look;
    if (!look) return;
    const t = v.now / 1000;
    const on = look.alarm && (v.calm ? Math.sin(t * Math.PI) > 0 : Math.sin(t * Math.PI * 5) > 0);
    // Slukkede byer har deres egen mørke sprite; resten tones mod skumring efter, hvor langt uret er.
    const dim = look.lit || (nv.bodyKey.endsWith('_dark') && look.dim >= 1) ? 0 : Math.min(0.75, look.dim + (on ? 0.2 : 0));
    nv.body.tint = mix(C.white, C.dusk, dim);
    if (look.alarm && !nv.heart) {
      const g = new Graphics();
      g.moveTo(0, 7).bezierCurveTo(-13, -1, -9, -13, 0, -6).bezierCurveTo(9, -13, 13, -1, 0, 7).closePath();
      g.fill(C.orange).stroke({ width: 2.5, color: C.ink, join: 'round' });
      g.position.set(-26, -44);
      nv.c.addChild(g);
      nv.heart = g;
    }
    if (!nv.heart) return;
    nv.heart.visible = look.alarm;
    if (!look.alarm) return;
    // Revner, når der næsten ingen tid er tilbage: hjertet er ved at ryge.
    const crack = look.clock >= 0.85;
    if ((nv.heart as Graphics & { cracked?: boolean }).cracked !== crack) {
      (nv.heart as Graphics & { cracked?: boolean }).cracked = crack;
      const g = nv.heart;
      g.clear();
      g.moveTo(0, 7).bezierCurveTo(-13, -1, -9, -13, 0, -6).bezierCurveTo(9, -13, 13, -1, 0, 7).closePath();
      g.fill(C.orange).stroke({ width: 2.5, color: C.ink, join: 'round' });
      if (crack) g.moveTo(0, -6).lineTo(-2.5, -1).lineTo(2, 2).lineTo(0, 6.5).stroke({ width: 1.8, color: C.ink, cap: 'round', join: 'round' });
    }
    nv.heart.scale.set(v.calm ? 1 : on ? 1.15 : 1);
    nv.heart.alpha = v.calm ? (on ? 1 : 0.7) : 1;
  }

  private drawUnder(nv: NodeView, n: GridNode, v: View, connected: boolean): void {
    const g = nv.under;
    g.clear();
    const t = v.now / 1000;
    const selected = v.selected === n.id;
    if (selected) {
      const r = 32 + (v.calm ? 0 : Math.sin(t * 5) * 2);
      g.ellipse(0, -12, r, r * 0.92).fill({ color: C.green, alpha: 0.18 }).stroke({ width: 3.5, color: C.green });
    } else if (v.targets?.has(n.id)) {
      // Gyldigt mål for ledningen fra den valgte node: så ser man, hvor langt man kan nå.
      const r = 26 + (v.calm ? 0 : Math.sin(t * 4 + n.x) * 1.5);
      g.ellipse(0, -12, r, r * 0.92).fill({ color: C.green, alpha: 0.12 }).stroke({ width: 2.5, color: C.green, alpha: 0.8 });
    } else if (!connected && n.kind !== 'foreign' && n.kind !== 'station') {
      // Ubundet node: en blød, pulserende invitation.
      const ph = v.calm ? 0.5 : (t * 0.9 + (n.x % 1)) % 1;
      const color = n.city ? C.yellow : C.green;
      g.ellipse(0, -12, 18 + ph * 20, (18 + ph * 20) * 0.9).stroke({ width: 3, color, alpha: 0.75 * (1 - ph) });
    }
    const c = n.city;
    if (c && !isLit(n) && (c.clock > 0 || c.out)) {
      const full = clockFullOf(v);
      const frac = c.out ? 1 : Math.min(1, c.clock / full);
      const r = 30;
      g.circle(0, -16, r).stroke({ width: 5.5, color: C.white });
      if (frac > 0.001) {
        g.moveTo(0, -16 - r).arc(0, -16, r, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2).stroke({ width: 4, color: C.red, cap: 'round' });
      }
      // Viseren på uret gør det til et ur, ikke kun en farve.
      const a = -Math.PI / 2 + frac * Math.PI * 2;
      g.moveTo(0, -16).lineTo(Math.cos(a) * (r - 7), -16 + Math.sin(a) * (r - 7)).stroke({ width: 2.4, color: C.red, cap: 'round', alpha: 0.85 });
    }
  }

  /**
   * En lille strømmåler ved siden af en by med ledning, der får for lidt: fyldt op til den andel af sin
   * strøm, den får lige nu. Så kan man se på kortet, hvem der mangler, og hvor meget, uden tal i MW.
   */
  private drawMeter(nv: NodeView, n: GridNode, v: View, connected: boolean): void {
    const g = nv.meter;
    g.clear();
    const c = n.city;
    if (!c || !connected || c.out || c.demandMw <= 0 || isLit(n) || shedNow(v.state, n)) {
      this.setMeterPlate(nv.id, null);
      return;
    }
    const f = Math.max(0, Math.min(1, c.servedMw / c.demandMw));
    const w = 11;
    const h = 32;
    const y = -40;
    // Spor: telefonkort: måleren står på den side af huset, hvor den ikke dækker naboens hus eller et
    // navn (i en tæt klynge på telefonen stod den oven i naboen). Siden holdes, så længe den er fri.
    // Er begge sider optaget (en naboes skilt, et navn), prøves et skridt længere ude, så over taget,
    // og ellers den plads, der dækker mindst.
    const side0 = Math.max(24, nv.body.width * 0.5 + 4);
    const sc = nv.c.scale.x;
    // Over taget: når begge sider er optaget (Aarhus og Malling på telefonen på tværs: eget navn på
    // den ene side, naboen på den anden).
    const roof = Math.min(y, nv.body.y + nv.body.getLocalBounds().minY * nv.body.scale.y - h - 4);
    const spots = [
      { dx: side0, dy: y },
      { dx: -side0, dy: y },
      { dx: side0 + 16, dy: y },
      { dx: -(side0 + 16), dy: y },
      { dx: 0, dy: roof },
      // Spor: telefonkort: skråt over taget og to skridt ude (Fredericia på bane 3: eget navn på den
      // ene side, Middelfart og Kolding på den anden, og hjertet over taget).
      { dx: side0, dy: roof },
      { dx: -side0, dy: roof },
      { dx: side0 + 32, dy: y },
      { dx: -(side0 + 32), dy: y },
      // Spor: telefonkort: lavere ved siden af huset (Holstebro på bane 1: Solpark Hjerms skilt på den
      // ene side i højde med måleren, Hovsøres navn på den anden og Vinderups navn over taget).
      { dx: side0, dy: y + 16 },
      { dx: -side0, dy: y + 16 },
    ];
    // I hvile (uden hop), som husene og de andre ting i regningen.
    const cy = nv.c.y + nv.lift;
    const at = (s: { dx: number; dy: number }) => ({ x0: nv.c.x + (s.dx - w / 2 - 5) * sc, y0: cy + (s.dy - 14) * sc, x1: nv.c.x + (s.dx + w / 2 + 5) * sc, y1: cy + (s.dy + h) * sc });
    const last = spots[nv.meterSpot ?? 0] as { dx: number; dy: number };
    const order = [last, ...spots.filter((s) => s !== last)];
    const cost = order.map((s) => this.plateCost(nv.id, at(s)));
    let best = cost.indexOf(0);
    if (best < 0) best = cost.reduce((bi, c, i) => (c < (cost[bi] as number) ? i : bi), 0);
    const spot = order[best] as { dx: number; dy: number };
    nv.meterSpot = spots.indexOf(spot);
    this.setMeterPlate(nv.id, at(spot));
    const x = spot.dx;
    const my = spot.dy;
    const pulse = v.calm ? 1 : 0.75 + 0.25 * Math.sin(v.now / 160);
    g.roundRect(x - w / 2, my, w, h, 5).fill(C.white).stroke({ width: 2.5, color: C.ink });
    const fh = (h - 6) * f;
    if (fh > 0.5) g.roundRect(x - w / 2 + 3, my + h - 3 - fh, w - 6, fh, 2).fill(C.green);
    // Det, der mangler, blinker orange øverst i måleren.
    const gap = h - 6 - fh;
    if (gap > 0.5) g.roundRect(x - w / 2 + 3, my + 3, w - 6, gap, 2).fill({ color: C.orange, alpha: 0.35 * pulse });
    // Et lille lyn over måleren.
    g.poly([x + 1, my - 13, x - 4, my - 4, x, my - 4, x - 1.5, my + 1, x + 4, my - 8, x, my - 8]).fill(C.orange).stroke({ width: 1.2, color: C.ink, join: 'round' });
  }

  private updateFace(nv: NodeView, n: GridNode, v: View): void {
    const c = n.city;
    if (!c || !nv.eyes || !nv.mouth || !nv.cheeks || !nv.zzz) return;
    const lit = isLit(n);
    let mood: Mood;
    if (lit) mood = 'happy';
    else if (c.out) mood = 'sad';
    // Nødaflastet: slukket med vilje. Den er sur, men sover ikke og går ikke i sort.
    else if (shedNow(v.state, n)) mood = 'angry';
    // Søvn er "ingen har talt til mig endnu". Med en ledning (også en under bygning) venter byen spændt.
    else if (c.clock <= 0) mood = (this.degree.get(n.id) ?? 0) > 0 ? 'worried' : 'sleep';
    else mood = c.clock / clockFullOf(v) > 0.5 ? 'angry' : 'worried';
    const now = v.now;
    if (mood !== nv.mood) {
      nv.mood = mood;
      nv.mouth.texture = v.atlas.tex(mood === 'sleep' ? 'mouth_sleep' : `mouth_${mood}`);
      nv.cheeks.visible = mood === 'happy';
      nv.zzz.visible = mood === 'sleep';
      if (nv.tear) nv.tear.visible = mood === 'sad';
    }
    this.updateCandles(nv, n, v, mood === 'sad');
    let eyes = mood === 'sleep' ? 'eyes_sleep' : mood === 'happy' ? 'eyes_open' : `eyes_${mood}`;
    if (mood !== 'sleep') {
      if (now > nv.blinkAt) {
        nv.blinkUntil = now + 130;
        nv.blinkAt = now + 2200 + Math.random() * 3800;
      }
      if (now < nv.blinkUntil) eyes = 'eyes_blink';
    }
    nv.eyes.texture = v.atlas.tex(eyes);
    if (nv.zzz.visible && !v.calm) {
      const t = (now / 1400 + (n.y % 1)) % 1;
      nv.zzz.position.set(16 + t * 4, -40 - t * 8);
      nv.zzz.alpha = Math.sin(t * Math.PI);
    }
    // Glade byer vipper en anelse, sure byer ryster.
    if (nv.face) {
      const t = now / 1000;
      nv.face.y = (n.kind === 'datacenter' ? -18 : -14) + (mood === 'happy' && !v.calm ? Math.sin(t * 3 + n.x) * 0.6 : 0);
      nv.face.x = mood === 'angry' && !v.calm ? Math.sin(t * 40) * 0.5 : 0;
    }
    // Tåren triller ned ad kinden og begynder forfra.
    if (nv.tear && nv.tear.visible) {
      const tt = v.calm ? 0.3 : ((now / 1600 + (n.x % 1)) % 1);
      nv.tear.position.set(-6.5, 1 + tt * 7);
      nv.tear.alpha = tt < 0.8 ? 1 : (1 - tt) * 5;
    }
    // Grøn strøm gør glad: byer med strøm hopper af og til, oftere jo grønnere nettet er.
    if (mood === 'happy' && !v.calm && this.green > 0.45 && now > nv.joyAt) {
      const g = Math.min(1, (this.green - 0.45) / 0.45);
      nv.joyAt = now + 3800 - g * 2200 + Math.random() * 2000;
      if (nv.hop.t >= 1) this.hop(n.id, 0, 3 + g * 4);
    }
  }

  /** Stearinlys i vinduerne, mens byen er i mørke efter en blackout. De blafrer en smule. */
  private updateCandles(nv: NodeView, n: GridNode, v: View, on: boolean): void {
    const tier = n.kind === 'datacenter' ? -2 : (n.city?.tier ?? 0);
    if (on && (!nv.candles || nv.candleTier !== tier)) {
      nv.candles?.destroy({ children: true });
      const box = new Container();
      for (const [x, y] of candleSpots(tier)) {
        const s = v.atlas.sprite('candle');
        s.position.set(x, y);
        box.addChild(s);
      }
      nv.c.addChildAt(box, 2);
      nv.candles = box;
      nv.candleTier = tier;
    }
    if (!nv.candles) return;
    nv.candles.visible = on;
    if (!on || v.calm) return;
    const t = v.now / 1000;
    nv.candles.children.forEach((s, i) => {
      const f = 0.85 + Math.sin(t * 9 + i * 2.1 + n.x) * 0.08 + Math.sin(t * 23 + i) * 0.05;
      s.alpha = f;
      s.scale.set(0.9 + f * 0.15);
    });
  }

  private updateBadges(nv: NodeView, n: GridNode, v: View): void {
    const want: string[] = [];
    if (n.station === 1) want.push('bolt');
    if (n.battery) want.push('battery');
    for (let i = 0; i < n.flywheels; i++) want.push('flywheel');
    // N-1-skjoldet som prikker ved husets højre fod (montøren holder til venstre): én gul (én vej ind), to grønne (to uafhængige veje).
    const dots = n.city && isLit(n) ? n.shield : 0;
    const key = `${want.join(',')}|${dots}`;
    if ((nv.badges as Container & { key?: string }).key === key) return;
    (nv.badges as Container & { key?: string }).key = key;
    nv.badges.removeChildren().forEach((c) => c.destroy());
    want.forEach((name, i) => {
      const s = v.atlas.sprite(name);
      s.anchor.set(0.5);
      s.scale.set(name === 'bolt' ? 0.55 : 0.9);
      s.position.set(-22 + i * 0, -8 - i * 16);
      nv.badges.addChild(s);
    });
    if (dots > 0) {
      const g = new Graphics();
      const color = dots === 2 ? C.green : C.yellow;
      for (let j = 0; j < dots; j++) g.circle(20 + j * 8, 4, 3.2).fill(color).stroke({ width: 1.4, color: C.ink });
      nv.badges.addChild(g);
    }
  }

  /** Skærmfelter, der er optaget af byer, møller og deres navne (seneste frame). */
  occupied: { x0: number; y0: number; x1: number; y1: number }[] = [];

  /**
   * Husenes kerner, navnene og de andre nodes små ting (skilte, hjerter, målere) med ejer (seneste
   * frame): dem går en nodes måler uden om.
   */
  private plates: Array<{ id: string; kind: 'house' | 'label' | 'object' | 'meter'; box: { x0: number; y0: number; x1: number; y1: number } }> = [];

  /**
   * Spor: telefonkort: en måler, der lige har valgt plads, står med sin nye plads i regningen for de
   * næste målere i samme billede. Før så Skanderborg Horsens' måler, hvor den stod i sidste billede,
   * og de to valgte samme hul mellem husene (bane 3 på telefonen).
   */
  private setMeterPlate(id: string, box: { x0: number; y0: number; x1: number; y1: number } | null): void {
    this.plates = this.plates.filter((p) => !(p.kind === 'meter' && p.id === id));
    if (box) this.plates.push({ id, kind: 'meter', box });
  }

  /** Hvor meget dækker feltet af en anden nodes hus og ting, eller af et navn (også sit eget)? */
  private plateCost(id: string, r: { x0: number; y0: number; x1: number; y1: number }): number {
    let cost = 0;
    for (const p of this.plates) {
      if (p.kind !== 'label' && p.id === id) continue;
      const b = p.box;
      const ow = Math.min(r.x1, b.x1) - Math.max(r.x0, b.x0);
      const oh = Math.min(r.y1, b.y1) - Math.max(r.y0, b.y0);
      if (ow > 1 && oh > 1) cost += ow * oh;
    }
    return cost;
  }

  /** Hvor hvert navn stod sidst (plads og form): den prøves først, så navnene ikke hopper. */
  private labelMem = emptyMemory();

  /** Testkrog (maptest DUMP): navnenes input i seneste billede, så en fejl kan genskabes uden browser. */
  lastLabels: { nodes: LabelNode[]; opts: LabelOpts; last: Array<[string, { slot: number; variant: number }]> } | null = null;

  /**
   * Navnene (labels.ts): ved egen node, aldrig oven i et andet navn eller hus, brudt eller forkortet,
   * før de dækker noget. Byer i nød først. Playtest runde 3: navne skiftede side og blev læst som
   * naboens, så et navn bliver på sin plads, så længe den er fri.
   */
  private placeLabels(v: View): void {
    const k = v.k;
    const nodes: LabelNode[] = [];
    const byId = new Map<string, NodeView>();
    for (const nv of this.views.values()) {
      if (!nv.c.visible || !nv.variants) continue;
      const p = v.pos.get(nv.id);
      if (!p) continue;
      // Husets kasse i hvile (uden hop og pop): spritens egne kanter gange containerens skala.
      const sc = k * 0.92;
      const lb = nv.body.getLocalBounds();
      const half = Math.max(14 * sc, Math.max(Math.abs(lb.minX), Math.abs(lb.maxX)) * sc);
      const top = p.y + lb.minY * sc;
      const bottom = p.y + lb.maxY * sc;
      byId.set(nv.id, nv);
      const must = nv.kind === 'city' || nv.kind === 'datacenter' || nv.kind === 'plant';
      nodes.push({ id: nv.id, x: p.x, y: p.y, half, top, bottom, prio: prio(nv, v), must, sizes: nv.variants.sizes });
    }
    const obstacles = this.obstacles.map((o) => ({ x0: o.x, y0: o.y, x1: o.x + o.w, y1: o.y + o.h }));
    const firm = this.firm.map((o) => ({ x0: o.x, y0: o.y, x1: o.x + o.w, y1: o.y + o.h }));
    const band = this.band ?? { x0: 0, y0: -1e4, x1: v.width, y1: 1e4 };
    // Spor: telefonkort: nodernes faste små ting (skilte, hjerter) står, hvor de står, så et navn går
    // uden om dem som om et hus (Ulstrups skilt lå på DC Hinnerups navn). Måleren flytter sig selv
    // efter navnene (drawMeter).
    const objects = this.objectBoxes()
      .filter((o) => o.type !== 'meter')
      .map((o) => ({ x0: o.box.x, y0: o.box.y, x1: o.box.x + o.box.w, y1: o.box.y + o.box.h }));
    this.lastLabels = { nodes, opts: { obstacles, band, objects, firm }, last: [...this.labelMem.last] };
    const placed = layoutLabels(nodes, { obstacles, band, objects, firm }, this.labelMem, (n) => {
      const nv = byId.get(n.id) as NodeView;
      return n.must || v.selected === n.id || v.named.has(n.id) || !!nv.urgent;
    });
    const taken: { x0: number; y0: number; x1: number; y1: number }[] = [];
    const plates: typeof this.plates = [];
    for (const n of nodes) {
      const cw = n.half * 0.64;
      const ch = (n.bottom - n.top) * 0.32;
      const cy = (n.top + n.bottom) / 2;
      plates.push({ id: n.id, kind: 'house', box: { x0: n.x - cw, y0: cy - ch, x1: n.x + cw, y1: cy + ch } });
    }
    for (const o of this.objectBoxes()) plates.push({ id: o.owner, kind: o.type === 'meter' ? 'meter' : 'object', box: { x0: o.box.x, y0: o.box.y, x1: o.box.x + o.box.w, y1: o.box.y + o.box.h } });
    for (const nv of this.views.values()) {
      const pl = placed.get(nv.id);
      if (!pl || !nv.variants) {
        nv.label.visible = false;
        continue;
      }
      plates.push({ id: nv.id, kind: 'label', box: pl.box });
      if (pl.variant !== nv.variant) {
        nv.variant = pl.variant;
        nv.label.style = LABEL_STYLES[pl.variant] as TextStyle;
        nv.label.text = nv.variants.texts[pl.variant] ?? nv.variants.name;
      }
      nv.label.position.set(pl.x, pl.y);
      nv.label.visible = true;
      nv.label.alpha = nv.pop.s;
      taken.push(pl.box);
    }
    this.occupied = [...nodes.map((n) => ({ x0: n.x - n.half, y0: n.top, x1: n.x + n.half, y1: n.bottom })), ...taken];
    this.plates = plates;
  }

  /** Skærmpunktet for en nodes "krop" (til lysbølger og hit-test). */
  center(id: string): P | null {
    const nv = this.views.get(id);
    if (!nv || !nv.c.visible) return null;
    return { x: nv.c.x, y: nv.c.y - 16 * nv.c.scale.y };
  }
}

/** Vinduerne i bytegningen (atlas.ts: cityBody), hvor stearinlysene står. */
function candleSpots(tier: number): [number, number][] {
  if (tier === -2) return [[-14, -6], [6, -6]];
  const w = 15 + tier * 1.2;
  const out: [number, number][] = [[0, -31 - tier * 0.4]];
  if (tier >= 1) out.push([-w - 7.5, -8]);
  if (tier >= 2) out.push([w + 8.5, -9.3]);
  if (tier >= 3) out.push([-20.2, -41], [-14.2, -23]);
  if (tier >= 4) out.push([9.8, -54], [15.8, -36]);
  return out;
}

/** Blander to farver: 0 er a, 1 er b. */
function mix(a: number, b: number, f: number): number {
  const ch = (x: number, s: number): number => (x >> s) & 0xff;
  const m = (s: number): number => Math.round(ch(a, s) + (ch(b, s) - ch(a, s)) * f) << s;
  return m(16) | m(8) | m(0);
}

function clockFullOf(v: View): number {
  return Math.max(1, v.clockFull);
}

function prio(nv: NodeView, v: View): number {
  if (v.selected === nv.id) return 100;
  if (v.named.has(nv.id)) return 90;
  if (nv.urgent) return 50;
  if (nv.kind === 'city' || nv.kind === 'datacenter') return 10;
  if (nv.kind === 'plant') return 8;
  if (nv.kind === 'foreign') return 1;
  return 5;
}
